<template>
  <div class="teacher-assignment-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إسناد المقررات الدراسية للمعلمين</h2>
        </div>
      </template>

      <el-alert
        title="لماذا هذه الواجهة مهمة؟"
        type="info"
        description="هذه الواجهة هي الخطوة الثانية في تجهيز العام الدراسي. بعد تعريف المقررات لكل صف، نقوم هنا بتحديد المعلم المسؤول عن تدريس كل مقرر. هذه الخطوة ضرورية لبناء الجداول الدراسية والسماح للمعلمين برصد الدرجات لاحقًا."
        :closable="false"
        show-icon
        style="margin-bottom: 20px;"
      />

      <el-input
        v-model="searchQuery"
        placeholder="ابحث عن معلم بالاسم..."
        @input="handleSearch"
        clearable
        class="search-input"
      >
        <template #prepend><el-icon><Search /></el-icon></template>
      </el-input>

      <el-table :data="teachers" v-loading="loading" border style="width: 100%; margin-top: 20px;">
        <el-table-column prop="name" label="اسم المعلم" />
        <el-table-column prop="email" label="البريد الإلكتروني" />
        <el-table-column prop="courses_count" label="عدد الإسنادات" width="180" />
        <el-table-column label="الإجراءات" width="180" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="handleManageAssignments(row)">
              <el-icon><Edit /></el-icon> إدارة الإسناد
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :current-page="pagination.currentPage"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- Dialog لإدارة المقررات -->
    <el-dialog v-model="dialogVisible" :title="`إدارة إسناد المعلم: ${selectedTeacher?.name}`" width="80%" @close="resetDialog">
      <div v-if="selectedTeacher">
        <el-tabs v-model="activeTab">
            <el-tab-pane label="الإسنادات الحالية" name="current">
                <el-table :data="assignedCourses" v-loading="coursesLoading" height="400px">
                    <el-table-column prop="course_offering.subject.name" label="المادة" />
                    <el-table-column prop="course_offering.school_class.name" label="الصف" />
                    <el-table-column prop="section.name" label="القسم" />
                    <el-table-column prop="course_offering.study_year.name" label="السنة الدراسية" />
                    <el-table-column label="إجراء" width="100">
                        <template #default="{ row }">
                            <el-button type="danger" size="small" @click="removeAssignment(row)">إزالة</el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </el-tab-pane>
            <el-tab-pane label="إضافة إسناد جديد" name="add">
                <el-row :gutter="20" style="margin-bottom: 20px;">
                    <el-col :span="8">
                        <el-select v-model="filters.study_year_id" placeholder="1. اختر السنة الدراسية" @change="fetchClassesForFilter" style="width: 100%;">
                            <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
                        </el-select>
                    </el-col>
                    <el-col :span="8">
                         <el-select v-model="filters.class_id" placeholder="2. اختر الفصل الدراسي" @change="fetchSectionsForFilter" :disabled="!filters.study_year_id" style="width: 100%;">
                            <el-option v-for="cls in classes" :key="cls.id" :label="cls.name" :value="cls.id" />
                        </el-select>
                    </el-col>
                    <el-col :span="8">
                         <el-select v-model="filters.section_id" placeholder="3. اختر القسم" @change="fetchAvailableCourses" :disabled="!filters.class_id" style="width: 100%;">
                            <el-option v-for="sec in sections" :key="sec.id" :label="sec.name" :value="sec.id" />
                        </el-select>
                    </el-col>
                </el-row>
                
                <el-table :data="availableCourses" v-loading="coursesLoading" height="300px" @selection-change="handleSelectionChange">
                    <el-table-column type="selection" width="55" />
                    <el-table-column prop="subject.name" label="المادة" />
                </el-table>
            </el-tab-pane>
        </el-tabs>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="saveAssignments" :loading="isSaving">
            <el-icon><Finished /></el-icon> حفظ التغييرات
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Search, Edit, Finished } from '@element-plus/icons-vue';
import debounce from 'lodash/debounce';

export default {
  name: 'AssignTeacherToCourse',
  components: { Search, Edit, Finished },
  data() {
    return {
      api: null,
      loading: true,
      isSaving: false,
      coursesLoading: false,
      teachers: [],
      searchQuery: '',
      selectedTeacher: null,
      dialogVisible: false,
      activeTab: 'current',
      
      assignedCourses: [],
      availableCourses: [],
      newlySelectedCourses: [],
      
      studyYears: [],
      classes: [],
      sections: [],
      filters: {
        study_year_id: null,
        class_id: null,
        section_id: null,
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      }
    };
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchTeachers = debounce(this.fetchTeachers, 300);
    this.fetchInitialData();
  },
  methods: {
    async fetchInitialData() {
        this.loading = true;
        try {
            await Promise.all([this.fetchTeachers(), this.fetchStudyYears()]);
        } catch (error) {
            ElMessage.error("فشل في جلب البيانات الأولية.");
        } finally {
            this.loading = false;
        }
    },
    async fetchTeachers() {
      this.loading = true;
      try {
        const response = await this.api.get('/assignments/teachers', { 
            params: { 
                search: this.searchQuery,
                page: this.pagination.currentPage 
            }
        });
        this.teachers = response.data.data;
        this.pagination.total = response.data.total;
        this.pagination.currentPage = response.data.current_page;
      } catch (error) {
        ElMessage.error('فشل في جلب قائمة المعلمين.');
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
        this.pagination.currentPage = 1;
        this.fetchTeachers();
    },
    handlePageChange(newPage) {
        this.pagination.currentPage = newPage;
        this.fetchTeachers();
    },
    async fetchStudyYears() {
        try {
          const response = await this.api.get('/study-years');
          this.studyYears = response.data.data || response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الأعوام الدراسية.');
        }
    },
    async fetchClassesForFilter() {
        this.filters.class_id = null;
        this.filters.section_id = null;
        this.availableCourses = [];
        this.sections = [];
        if (!this.filters.study_year_id) return;
        try {
            const response = await this.api.get('/classes', { params: { study_year_id: this.filters.study_year_id } });
            this.classes = response.data;
        } catch (error) {
            ElMessage.error("فشل في جلب الفصول الدراسية.");
        }
    },
    async fetchSectionsForFilter() {
        this.filters.section_id = null;
        this.availableCourses = [];
        if (!this.filters.class_id) return;
        try {
            const response = await this.api.get(`/classes/${this.filters.class_id}/sections-for-assign`);
            this.sections = response.data;
        } catch (error) {
            ElMessage.error("فشل في جلب الأقسام.");
        }
    },
    async fetchAvailableCourses() {
        if (!this.filters.section_id || !this.selectedTeacher) return;
        this.coursesLoading = true;
        try {
            const params = {
                ...this.filters,
                teacher_id: this.selectedTeacher.id
            };
            const response = await this.api.get('/assignments/available-courses', { params });
            this.availableCourses = response.data;
        } catch (error) {
            ElMessage.error("فشل في جلب المقررات المتاحة.");
        } finally {
            this.coursesLoading = false;
        }
    },
    async handleManageAssignments(teacher) {
      this.selectedTeacher = teacher;
      this.dialogVisible = true;
      this.activeTab = 'current';
      this.coursesLoading = true;
      try {
        const response = await this.api.get(`/teachers/${teacher.id}/assignments`);
        this.assignedCourses = response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب المقررات المسندة.');
      } finally {
        this.coursesLoading = false;
      }
    },
    removeAssignment(assignmentToRemove) {
        this.assignedCourses = this.assignedCourses.filter(a => a.id !== assignmentToRemove.id);
    },
    handleSelectionChange(selection) {
        this.newlySelectedCourses = selection;
    },
    async saveAssignments() {
      if (!this.selectedTeacher) return;
      this.isSaving = true;

      // --- THE FIX IS HERE ---
      // 1. Filter current assignments to only include valid ones (with a section_id)
      const currentAssignments = this.assignedCourses
        .filter(a => a.section_id) // This will ignore old/invalid data
        .map(a => ({
          course_offering_id: a.course_offering_id,
          section_id: a.section_id,
      }));
      
      // 2. Prepare the newly selected assignments
      const newAssignments = this.newlySelectedCourses.map(course => ({
          course_offering_id: course.id,
          section_id: this.filters.section_id,
      }));

      // 3. Combine them to create the final list
      const finalAssignments = [...currentAssignments, ...newAssignments];

      try {
        await this.api.post(`/teachers/${this.selectedTeacher.id}/assignments`, {
          assignments: finalAssignments,
        });
        ElMessage.success('تم تحديث إسناد المقررات بنجاح!');
        this.dialogVisible = false;
        this.fetchTeachers();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشلت عملية الحفظ.');
      } finally {
        this.isSaving = false;
      }
    },
    resetDialog() {
      this.selectedTeacher = null;
      this.filters = { study_year_id: null, class_id: null, section_id: null };
      this.classes = [];
      this.sections = [];
      this.availableCourses = [];
      this.assignedCourses = [];
      this.newlySelectedCourses = [];
    }
  },
};
</script>




<style scoped>
.teacher-assignment-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.search-input { max-width: 400px; }
.pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}
:deep(.el-transfer-panel) { width: 40% !important; height: 400px !important; }
:deep(.el-transfer-panel__body) { height: 350px !important; }
</style>
